/**
 * Sistem Ayarları › SMS Bakiye ve Ayarlar (backlog #637, backend #635 / #631 / #636). Üç parça:
 *   1) BAKİYE: kalan kredi, birim fiyat, son okunma; düşük bakiye uyarısı (eşiğin altında) ve "kredi yok" durumu; "Yenile" satıcıdan şimdi okur.
 *   2) ANAHTARLAR: Sistem Ayarları'nın `sms` grubu (sms.etkin ana şalter + kullanım başına şalterler, hepsi KAPALI başlar #631; ve backend'in eklediği sayısal ayarlar,
 *      ör. bakiye uyarı eşiği). Bir şalter KAPALIYKEN o kullanımın SMS'i gitmez ve kayda "bloke" düşer; ana şalter her şeyi keser. Açmak SYSTEM_SETTINGS_MANAGE ister.
 *   3) RAPOR ADRESİ (#635): satıcının teslim raporlarını gönderdiği gizli adres. Adres imzasızdır — bilen herkes rapor uydurabilir — bu yüzden uzun rastgele bir sır
 *      içerir ve buradan DEĞİŞTİRİLEBİLİR; tam adres yalnız değiştirildiği anda bir kez gösterilir, satıcının panelindeki Rapor URL'sine yapıştırılır (hesap başına tek adres).
 */
import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import SmsSayfa from './SmsSayfa';
import { getBalance, getReportSecret, refreshBalance, rotateReportSecret } from '../../../../services/smsAyarService';
import { listSystemSettings, updateSystemSetting } from '../../../../services/systemSettingsService';
import { currentLocaleTag } from '../../../../i18n';

const tarih = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString(currentLocaleTag()) : '';
};
const para = (n, birim) => (n === null || n === undefined || n === '' ? '—' : `${Number(n).toLocaleString(currentLocaleTag(), { maximumFractionDigits: 4 })}${birim ? ` ${birim}` : ''}`);
const mantiksal = (s) => String(s.type || '').toUpperCase() === 'BOOLEAN';

const SmsBakiyePage = () => {
  const { t } = useTranslation('sms');
  const [bakiye, setBakiye] = useState(null);
  const [bakiyeHatasi, setBakiyeHatasi] = useState(null);
  const [yenileniyor, setYenileniyor] = useState(false);
  const [ayarlar, setAyarlar] = useState(null);
  const [ayarHatasi, setAyarHatasi] = useState(null);
  const [kaydedilen, setKaydedilen] = useState(null);   // anahtar
  const [degerler, setDegerler] = useState({});          // sayısal alanların yazılan değeri
  const [rapor, setRapor] = useState(null);              // { url, rotated_at }
  const [raporHatasi, setRaporHatasi] = useState(null);
  const [yeniAdres, setYeniAdres] = useState(null);      // değiştirme sonucu: tam adres, yalnız bir kez
  const [degistirOnay, setDegistirOnay] = useState(false);
  const [degistiriyor, setDegistiriyor] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);

  useEffect(() => {
    let iptal = false;
    async function calistir() {
      const [b, a, r] = await Promise.allSettled([getBalance(), listSystemSettings('sms'), getReportSecret()]);
      if (iptal) return;
      if (b.status === 'fulfilled') setBakiye(b.value); else setBakiyeHatasi(b.reason.message);
      if (a.status === 'fulfilled') {
        setAyarlar(a.value);
        setDegerler(Object.fromEntries(a.value.filter((s) => !mantiksal(s)).map((s) => [s.key, s.value === null || s.value === undefined ? '' : String(s.value)])));
      } else setAyarHatasi(a.reason.message);
      if (r.status === 'fulfilled') setRapor(r.value); else setRaporHatasi(r.reason.message);
    }
    calistir();
    return () => { iptal = true; };
  }, []);

  async function bakiyeYenile() {
    setYenileniyor(true);
    setBakiyeHatasi(null);
    try {
      setBakiye(await refreshBalance());
    } catch (e) {
      setBakiyeHatasi(e.message);
    } finally {
      setYenileniyor(false);
    }
  }

  async function ayarKaydet(s, deger) {
    setKaydedilen(s.key);
    setAyarHatasi(null);
    try {
      const yeni = await updateSystemSetting(s.key, deger);
      setAyarlar((liste) => liste.map((x) => (x.key === s.key ? { ...x, ...(yeni && typeof yeni === 'object' ? yeni : {}), value: yeni?.value ?? deger } : x)));
    } catch (e) {
      setAyarHatasi(e.message);
    } finally {
      setKaydedilen(null);
    }
  }

  async function adresiDegistir() {
    setDegistiriyor(true);
    setRaporHatasi(null);
    try {
      const sonuc = await rotateReportSecret();
      setYeniAdres(sonuc.url);
      setRapor({ url: null, rotated_at: sonuc.rotated_at, has_secret: true });
      setKopyalandi(false);
      setDegistirOnay(false);
    } catch (e) {
      setRaporHatasi(e.message);
      setDegistirOnay(false);
    } finally {
      setDegistiriyor(false);
    }
  }

  async function kopyala() {
    try {
      await navigator.clipboard.writeText(yeniAdres);
      setKopyalandi(true);
    } catch {
      setKopyalandi(false);
    }
  }

  const ana = ayarlar && ayarlar.find((s) => s.key === 'sms.etkin');

  return (
    <SmsSayfa aktif="bakiye">
      <Row>
        <Col lg={6}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">{t('bakiye.baslik')}</h5>
              <Button size="sm" color="light" id="sms-bakiye-yenile" disabled={yenileniyor} onClick={bakiyeYenile}>
                {yenileniyor ? <Spinner size="sm" /> : <><i className="bx bx-refresh me-1" />{t('bakiye.yenile')}</>}
              </Button>
            </CardHeader>
            <CardBody data-testid="sms-bakiye-karti">
              {bakiyeHatasi && <Alert color="danger" fade={false} data-testid="sms-bakiye-hata">{bakiyeHatasi}</Alert>}
              {!bakiye && !bakiyeHatasi && <div className="text-center py-3"><Spinner /></div>}
              {bakiye && (
                <>
                  {bakiye.no_credit && <Alert color="danger" fade={false} data-testid="sms-kredi-yok">{t('bakiye.krediYok')}</Alert>}
                  {!bakiye.no_credit && bakiye.low && <Alert color="warning" fade={false} data-testid="sms-bakiye-dusuk">{t('bakiye.dusuk', { esik: para(bakiye.low_threshold, bakiye.currency) })}</Alert>}
                  <div className="d-flex gap-4 flex-wrap">
                    <div><div className="text-muted small">{t('bakiye.kalan')}</div><div className="fs-3 fw-semibold" data-testid="sms-bakiye-tutar">{para(bakiye.balance, bakiye.currency)}</div></div>
                    <div><div className="text-muted small">{t('bakiye.birimFiyat')}</div><div className="fs-5" data-testid="sms-birim-fiyat">{para(bakiye.unit_price, bakiye.currency)}</div></div>
                  </div>
                  {bakiye.checked_at && <div className="small text-muted mt-2">{t('bakiye.okunma', { zaman: tarih(bakiye.checked_at) })}</div>}
                </>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col lg={6}>
          <Card>
            <CardHeader><h5 className="card-title mb-0">{t('bakiye.raporBasligi')}</h5></CardHeader>
            <CardBody data-testid="sms-rapor-karti">
              <p className="text-muted small">{t('bakiye.raporAciklama')}</p>
              {raporHatasi && <Alert color="danger" fade={false}>{raporHatasi}</Alert>}
              {yeniAdres && (
                <Alert color="success" fade={false} data-testid="sms-yeni-adres">
                  <div className="fw-semibold mb-1">{t('bakiye.yeniAdres')}</div>
                  <code className="user-select-all d-block text-break mb-2">{yeniAdres}</code>
                  <Button size="sm" color="light" id="sms-adres-kopyala" onClick={kopyala}>{kopyalandi ? t('bakiye.kopyalandi') : t('bakiye.kopyala')}</Button>
                  <div className="small mt-2">{t('bakiye.yeniAdresNotu')}</div>
                </Alert>
              )}
              {!yeniAdres && rapor && (
                <p className="mb-2" data-testid="sms-rapor-durum">
                  {rapor.has_secret === false ? t('bakiye.raporYok') : t('bakiye.raporVar', { zaman: tarih(rapor.rotated_at) || '—' })}
                  {rapor.url ? <code className="d-block text-break mt-1">{rapor.url}</code> : null}
                </p>
              )}
              <Button color="warning" outline id="sms-adres-degistir" onClick={() => setDegistirOnay(true)}>{t('bakiye.adresiDegistir')}</Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card>
        <CardHeader><h5 className="card-title mb-0">{t('bakiye.anahtarlar')}</h5></CardHeader>
        <CardBody data-testid="sms-anahtarlar">
          <p className="text-muted small">{t('bakiye.anahtarlarAciklama')}</p>
          {ayarHatasi && <Alert color="danger" fade={false} data-testid="sms-ayar-hata">{ayarHatasi}</Alert>}
          {!ayarlar && !ayarHatasi && <div className="text-center py-3"><Spinner /></div>}
          {ana && !ana.value && <Alert color="secondary" fade={false} className="py-2" data-testid="sms-ana-kapali">{t('bakiye.anaKapali')}</Alert>}
          {ayarlar && ayarlar.map((s) => (
            <div className="d-flex align-items-center justify-content-between border-bottom py-2" key={s.key} data-testid={`sms-ayar-${s.key}`}>
              <div className="me-3">
                <div className="fw-semibold">{s.label || s.key}{s.key === 'sms.etkin' && <Badge color="danger" className="ms-2">{t('bakiye.anaSalter')}</Badge>}</div>
                <div className="small text-muted">{s.key}{s.description ? ` — ${s.description}` : ''}</div>
              </div>
              {mantiksal(s) ? (
                <div className="form-check form-switch fs-4 mb-0">
                  <Input className="form-check-input" type="checkbox" role="switch" id={`sms-ayar-anahtar-${s.key}`} checked={s.value === true || s.value === 'true'}
                    disabled={kaydedilen === s.key} onChange={(e) => ayarKaydet(s, e.target.checked)} aria-label={s.label || s.key} />
                </div>
              ) : (
                <div className="d-flex gap-2" style={{ width: 220 }}>
                  <Label htmlFor={`sms-ayar-deger-${s.key}`} className="visually-hidden">{s.label || s.key}</Label>
                  <Input id={`sms-ayar-deger-${s.key}`} type="number" min={s.min ?? undefined} max={s.max ?? undefined} value={degerler[s.key] ?? ''}
                    onChange={(e) => setDegerler((d) => ({ ...d, [s.key]: e.target.value }))} />
                  <Button color="primary" id={`sms-ayar-kaydet-${s.key}`} disabled={kaydedilen === s.key || degerler[s.key] === '' || degerler[s.key] === String(s.value)}
                    onClick={() => ayarKaydet(s, Number(degerler[s.key]))}>{kaydedilen === s.key ? <Spinner size="sm" /> : t('kaydet')}</Button>
                </div>
              )}
            </div>
          ))}
        </CardBody>
      </Card>

      <Modal isOpen={degistirOnay} toggle={() => !degistiriyor && setDegistirOnay(false)} centered>
        <ModalHeader toggle={() => setDegistirOnay(false)}>{t('bakiye.degistirBasligi')}</ModalHeader>
        <ModalBody>{t('bakiye.degistirSorusu')}</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDegistirOnay(false)}>{t('vazgec')}</Button>
          <Button color="warning" id="sms-adres-degistir-onayla" disabled={degistiriyor} onClick={adresiDegistir}>{degistiriyor ? <Spinner size="sm" /> : t('bakiye.adresiDegistir')}</Button>
        </ModalFooter>
      </Modal>
    </SmsSayfa>
  );
};

export default SmsBakiyePage;
